import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import PublicLayout from "../components/PublicLayout";
import SEO from "../components/SEO";
import { api } from "../lib/api";
import { MapPin, Phone, Check, HelpCircle, ArrowRight, Loader2 } from "lucide-react";
import WhatsAppIcon from "../components/WhatsAppIcon";

export default function SeoPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let ok = true;
    setStatus("loading");
    const lang = localStorage.getItem("lang") || "en";
    // If Marathi is selected and a `-mr` twin exists, prefer it
    const tryFetch = async () => {
      if (lang === "mr" && !slug.endsWith("-mr")) {
        try {
          const r = await api.get(`/seo-pages/public/${slug}-mr`);
          if (ok) { setPage(r.data); setStatus("ok"); }
          return;
        } catch { /* fall through to default slug */ }
      }
      try {
        const r = await api.get(`/seo-pages/public/${slug}`);
        if (ok) { setPage(r.data); setStatus("ok"); }
      } catch {
        if (ok) setStatus("not-found");
      }
    };
    tryFetch();
    // Fire-and-forget view tracker for the weekly SEO report
    api.post(`/seo-pages/track-view/${slug}`).catch(() => {});
    return () => { ok = false; };
  }, [slug]);

  if (status === "loading") return <PublicLayout><div className="py-24 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-[#ea580c]" /></div></PublicLayout>;
  if (status === "not-found") return <PublicLayout>
    <section className="max-w-2xl mx-auto px-4 py-24 text-center">
      <h1 className="text-3xl font-display font-black">Page not found</h1>
      <p className="text-slate-500 mt-2">This landing page doesn't exist or is inactive.</p>
      <Link to="/" className="mt-4 inline-block px-5 py-2 rounded-full bg-[#ea580c] text-white font-bold">Back home</Link>
    </section>
  </PublicLayout>;

  const jsonLd = buildJsonLd(page);
  const waNumber = "919890201047";
  const waMsg = encodeURIComponent(page.whatsapp_cta_text || `Hi Shruti Tours, I'm interested in "${page.h1}". Please share details.`);

  return (
    <PublicLayout>
      <SEO
        title={page.meta_title}
        description={page.meta_description}
        ogImage={page.og_image || page.hero_image}
        keywords={[page.origin, page.destination, page.city, page.vehicle_type, "Shruti Tours"].filter(Boolean).join(", ")}
        jsonLd={jsonLd}
        lang={page.lang}
      />

      {/* Hero */}
      <section className="relative">
        {page.hero_image && (
          <img src={page.hero_image} alt={page.h1} className="w-full h-56 md:h-80 object-cover" />
        )}
        <div className="max-w-5xl mx-auto px-4 py-10">
          <div className="text-[10px] uppercase tracking-widest text-[#ea580c] font-bold flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {page.kind === "route" ? `${page.origin || ""} → ${page.destination || ""}` : (page.city || page.kind)}
          </div>
          <h1 className="mt-2 text-3xl md:text-5xl font-display font-black tracking-tight text-[#1e1b4b]" data-testid="seo-h1">{page.h1}</h1>
          {page.subtitle && <p className="mt-2 text-lg text-slate-600">{page.subtitle}</p>}
          {page.price_from && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-bold">
              From ₹{Number(page.price_from).toLocaleString()} {page.price_note && <span className="text-emerald-700 font-normal">· {page.price_note}</span>}
            </div>
          )}
          <div className="mt-6 flex flex-wrap gap-2">
            <Link to={page.primary_cta_url || "/custom-booking"} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#ea580c] hover:bg-orange-700 text-white font-bold text-sm shadow-lg" data-testid="seo-primary-cta">
              {page.primary_cta_label || "Get Instant Quote"} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`https://wa.me/${waNumber}?text=${waMsg}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm" data-testid="seo-wa-cta">
              <WhatsAppIcon className="w-4 h-4" /> WhatsApp
            </a>
            <a href="tel:+919890201047" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1e1b4b] hover:bg-[#14205a] text-white font-bold text-sm" data-testid="seo-call-cta">
              <Phone className="w-4 h-4" /> Call
            </a>
          </div>
        </div>
      </section>

      {/* Key points / inclusions */}
      {(page.key_points?.length > 0 || page.inclusions?.length > 0) && (
        <section className="max-w-5xl mx-auto px-4 pb-6">
          <div className="grid md:grid-cols-2 gap-6">
            {page.key_points?.length > 0 && (
              <div className="p-5 rounded-2xl bg-white border border-slate-200">
                <div className="font-display font-bold text-[#1e1b4b]">Highlights</div>
                <ul className="mt-3 space-y-2 text-sm">
                  {page.key_points.map((k, i) => <li key={`k-${i}`} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {k}</li>)}
                </ul>
              </div>
            )}
            {page.inclusions?.length > 0 && (
              <div className="p-5 rounded-2xl bg-white border border-slate-200">
                <div className="font-display font-bold text-[#1e1b4b]">What's included</div>
                <ul className="mt-3 space-y-2 text-sm">
                  {page.inclusions.map((k, i) => <li key={`i-${i}`} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#ea580c] mt-0.5 shrink-0" /> {k}</li>)}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Body content */}
      {page.body_html && (
        <section className="max-w-3xl mx-auto px-4 pb-8">
          <div className="prose prose-slate max-w-none seo-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.body_html, { USE_PROFILES: { html: true } }) }} />
        </section>
      )}

      {/* FAQs */}
      {page.faqs?.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 pb-12" data-testid="seo-faqs">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#ea580c] font-bold">
            <HelpCircle className="w-3 h-3" /> Frequently asked
          </div>
          <h2 className="text-2xl md:text-3xl font-display font-black tracking-tight mt-1">Common questions</h2>
          <div className="mt-4 space-y-2">
            {page.faqs.map((f, i) => (
              <details key={`faq-${f.q?.slice(0,20)}-${i}`} className="p-4 rounded-xl bg-white border border-slate-200 open:border-[#ea580c] group">
                <summary className="cursor-pointer font-semibold text-[#1e1b4b] list-none flex justify-between items-center">
                  <span>{f.q}</span>
                  <span className="text-[#ea580c] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Related links (internal linking) */}
      {page.related_slugs?.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 pb-16" data-testid="seo-related">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">You may also like</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {page.related_slugs.map(s => (
              <Link key={s} to={`/seo/${s}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-[#ea580c] hover:text-[#ea580c] text-sm text-slate-700 transition-colors">
                {s.replace(/-/g, " ")}
              </Link>
            ))}
          </div>
        </section>
      )}
    </PublicLayout>
  );
}

function buildJsonLd(p) {
  const out = [];
  // Breadcrumb
  const home = { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin + "/" };
  const current = { "@type": "ListItem", position: 2, name: p.h1, item: window.location.href.split("?")[0] };
  out.push({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [home, current] });

  // Service or Trip
  if (p.kind === "route" && p.origin && p.destination) {
    out.push({
      "@context": "https://schema.org",
      "@type": "TouristTrip",
      name: p.h1,
      description: p.meta_description,
      touristType: ["Family", "Group", "Business"],
      itinerary: { "@type": "ItemList", itemListElement: [
        { "@type": "Place", name: p.origin },
        { "@type": "Place", name: p.destination },
      ]},
      provider: { "@type": "TravelAgency", name: "Shruti Tours & Travels", telephone: "+919890201047" },
      offers: p.price_from ? { "@type": "Offer", price: p.price_from, priceCurrency: "INR" } : undefined,
    });
  } else if (p.schema_service_type || p.kind === "vehicle" || p.kind === "service") {
    out.push({
      "@context": "https://schema.org",
      "@type": "Service",
      name: p.h1,
      description: p.meta_description,
      serviceType: p.schema_service_type || p.vehicle_type || "Vehicle Rental",
      areaServed: { "@type": "State", name: "Maharashtra" },
      provider: { "@type": "TravelAgency", name: "Shruti Tours & Travels", telephone: "+919890201047" },
      offers: p.price_from ? { "@type": "Offer", price: p.price_from, priceCurrency: "INR" } : undefined,
    });
  }

  // FAQ page
  if (p.faqs?.length > 0) {
    out.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: p.faqs.map(f => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  return out;
}
